import {
  AfterViewInit,
  Component,
  HostListener,
  OnInit
} from "@angular/core";
import {
  Question,
  QuestionnaireTemplate,
} from "../../models/questionnaire.model";
import { QuestionnaireService } from "../../services/questionnaire.service";
import { AuthService } from "../../services/auth.service";
import { Router } from "@angular/router";
import { NgForm } from "@angular/forms";
import { ResponseService } from "../../services/response.service";
import { ContentService } from "../../services/content.service";
import {
  DonorProfile,
  ProfileInfo,
  RecipientProfile,
} from "../../models/donorProfile.model";
import { NavigationService } from "../../services/navigation.service";
import { LocationStrategy } from "@angular/common";
import { NotificationsService } from "../../services/notifications.service";
import { calculateAge, formatSubmissionDate } from "../../utils/utils";
import { JourneySectionHelperService } from "../../services/journey-section-helper.service";
import { catchError, forkJoin, map, Observable, of, switchMap } from "rxjs";
import { NotificationRequest } from "../../models/notification.model";
import { LocaleService } from "../../services/locale.service";

const questionToProfileFieldMap: Record<string, keyof DonorProfile> = {
  "First Name": "firstName",
  "Last Name": "lastName",
  "Your Date of Birth": "dateOfBirth",
  Sex: "gender",
  Ethnicity: "ethnicity",
  Relationship: "relationshipToRecipient",
  "Blood type": "bloodGroup",
};

const initialQuestionnaireCopy = {
  en: {
    heading: "Initial Eligibility Questionnaire",
    instructionsBefore: "Please answer all the questions and click",
    submitLabel: "Submit",
    instructionsAfter: "when you are ready.",
    loadingQuestions: "Loading questions...",
    adjustingQuestions: "Adjusting questions based on your responses...",
    savingResponses: "Saving responses...",
    savedSuccess:
      "Your responses for Initial Eligibility Questionnaire have been saved successfully!",
    redirectingBefore: "Redirecting in",
    redirectingAfter: "seconds...",
    errorHeading: "Oh snap!",
    explanationText: [
      "Every transplant program has set minimal criteria for kidney donation. Candidates who meet these initial criteria can move forward in the more comprehensive process.",
      "Based on your answers to this initial questionnaire you may or may not be able to move forward to the next step.",
    ],
  },
  es: {
    heading: "Cuestionario Inicial de Elegibilidad",
    instructionsBefore: "Responda todas las preguntas y haga clic en",
    submitLabel: "Enviar",
    instructionsAfter: "cuando esté listo.",
    loadingQuestions: "Cargando preguntas...",
    adjustingQuestions: "Ajustando las preguntas según sus respuestas...",
    savingResponses: "Guardando respuestas...",
    savedSuccess:
      "Sus respuestas del Cuestionario Inicial de Elegibilidad se han guardado correctamente.",
    redirectingBefore: "Redirigiendo en",
    redirectingAfter: "segundos...",
    errorHeading: "Algo salió mal.",
    explanationText: [
      "Cada programa de trasplante tiene criterios mínimos establecidos para la donación de riñón. Los candidatos que cumplen con estos criterios iniciales pueden avanzar en el proceso más completo.",
      "Según sus respuestas a este cuestionario inicial, es posible que pueda o no avanzar al siguiente paso.",
    ],
  },
};

@Component({
  selector: "app-initial-questionnaire",
  templateUrl: "./initial-questionnaire.component.html",
  styleUrl: "./initial-questionnaire.component.scss",
})
export class InitialQuestionnaireComponent implements OnInit, AfterViewInit {
  questionnaireForm!: NgForm;
  questionnaire: QuestionnaireTemplate | null = null;
  questionnaireId: string | null = null;
  templateId = "QT_3407cee8-027d-419e-9171-fae52d2753c9";
  templateName = "initialQuestionnaire";
  isLoadingQuestionnaire = true;
  isLoadingResponses = true;
  isSavingResponses = false;
  hasCompleted = false;
  errorMessage: string | null = null;
  redirectTime = 5;
  donorSectionId = "";
  isIneligible = false;
  ineligibilityMessage = "";
  isContentScrollable = false;
  private readonly timingStoragePrefix = "questionnaireTiming";

  get copy() {
    return (
      initialQuestionnaireCopy[
        this.localeService.currentLocale as keyof typeof initialQuestionnaireCopy
      ] || initialQuestionnaireCopy.en
    );
  }

  constructor(
    private questionnaireService: QuestionnaireService,
    private authService: AuthService,
    private resService: ResponseService,
    private contentService: ContentService,
    private navigationService: NavigationService,
    private journeyHelper: JourneySectionHelperService,
    private notificationsService: NotificationsService,
    private router: Router,
    private locationStrategy: LocationStrategy,
    private localeService: LocaleService
  ) {}

  ngOnInit(): void {
    this.syncLocaleFromDonorProfile().subscribe(() => {
      this.loadQuestionnaire();
      this.getQuestionnaireByUserId();
    });
  }

  @HostListener("window:scroll", [])
  onWindowScroll() {
    this.updateScrollIndicator();
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.updateScrollIndicator(), 3000);
  }

  private updateScrollIndicator() {
    const doc = document.documentElement;
    const scrollTop = window.pageYOffset || doc.scrollTop;
    const viewportHeight = window.innerHeight;
    const totalHeight = doc.scrollHeight;

    // 5px buffer so you don’t turn it off too early
    this.isContentScrollable = scrollTop + viewportHeight < totalHeight - 5;
  }

  scrollDown(): void {
    window.scrollBy({
      top: window.innerHeight,
      behavior: "smooth",
    });
  }

  onFormInstanceCaptured(form: NgForm) {
    this.questionnaireForm = form;
  }

  private loadQuestionnaire(): void {
    this.errorMessage = null;
    this.questionnaireService
      .getQuestionnaireByName(this.templateName)
      .subscribe({
        next: (data) => {
          this.questionnaire = data.template; // Map response to the model
          this.isLoadingQuestionnaire = false;
          this.tryStartTiming();
        },
        error: (err) => {
          console.error("Error loading questionnaire:", err);
          this.errorMessage =
            "An error occured while loading the questionnaire. Please try again later.";
          this.isLoadingQuestionnaire = false;
        },
      });
  }

  private syncLocaleFromDonorProfile(): Observable<void> {
    return this.contentService.getDonorProfile().pipe(
      map((profileInfo: ProfileInfo) => {
        this.localeService.setLocale(
          profileInfo?.data?.donor?.locale ||
            profileInfo?.data?.donor?.preferredLocale
        );
        return void 0;
      }),
      catchError(() => of(void 0))
    );
  }

  private getQuestionnaireByUserId() {
    this.hasCompleted = false;
    this.isLoadingResponses = true;
    this.resService.getQuestionnaireByUserId().subscribe({
      next: (data: any) => {
        this.isLoadingResponses = false;
        if (data && data.length > 0) {
          const currentQuestionnaire = data.find(
            (response: any) => response.templateId === this.templateId
          );

          if (currentQuestionnaire) {
            // Redirect to Ineligible Screen!
            if (currentQuestionnaire.isIneligible) {
              this.navigationService.setIneligibilityDetails(
                {message: currentQuestionnaire.ineligibilityMessage}
              );
              this.router
                .navigate(["/questionnaire/postinitialineligible"])
                .then(() => {
                  this.locationStrategy.replaceState(
                    {},
                    "",
                    "/questionnaire/postinitialineligible",
                    ""
                  );
                });
              return;
            }

            this.hasCompleted =
              currentQuestionnaire.status.toLowerCase() === "completed";

            this.questionnaireId = currentQuestionnaire.questionnaireId;
            this.tryStartTiming();

            if (this.questionnaireId) {
              this.getAndInitializeJourneySectionsAndProfile(
                currentQuestionnaire.status
              );
            }

            if (this.hasCompleted) {
              this.startCountdown();
            }
            console.log(
              `Found questionnaire, setting questionnaire Id to ${this.questionnaireId}`
            );
          } else {
            console.log(
              "No responses found for the user, intializing new response"
            );

            this.resService
              .initializeQuestionnaireResponse(this.templateId)
              .subscribe({
                next: (data) => {
                  this.questionnaireId = data.questionnaireId;
                  this.tryStartTiming();
                  console.log(
                    `Created questionnaire, setting questionnaire Id to ${this.questionnaireId}`
                  );
                  if (this.questionnaireId) {
                    this.getAndInitializeJourneySectionsAndProfile(data.status);
                  }
                },
                error: (error) => {
                  console.log("Failed to initialize questionnaire response");
                  console.log(error);
                },
              });
          }
        } else {
          // Handle case where no responses are returned
          console.log(
            "No responses found for the user, intializing new response"
          );

          this.resService
            .initializeQuestionnaireResponse(this.templateId)
            .subscribe({
              next: (data) => {
                console.log("questionnaire id: ", this.questionnaireId);
                this.questionnaireId = data.questionnaireId;
                this.tryStartTiming();
                if (this.questionnaireId) {
                  this.getAndInitializeJourneySectionsAndProfile(data.status);
                }
              },
              error: (error) => {
                console.log("Failed to initialize questionnaire response");
                console.log(error);
              },
            });
        }
      },
      error: (err) => {
        console.log("Failed to get Questionnaire Responses for User");
      },
    });
  }

  private getAndInitializeJourneySectionsAndProfile(sectionStatus: string) {
    this.journeyHelper.ensureJourneySectionsExist().subscribe({
      next: (sections) => {
        this.donorSectionId = sections[0]?.sectionId;

        if (this.questionnaireId && !sections[0].questionnaireId) {
          this.journeyHelper
            .linkQuestionnaireToSection(
              this.donorSectionId,
              this.questionnaireId
            )
            .subscribe();
        }

        if (sectionStatus !== "In Progress") {
          console.log("section status: ", sectionStatus);
          this.journeyHelper
            .updateSectionStatus(this.donorSectionId, sectionStatus)
            .subscribe();
        }
      },
    });
    this.journeyHelper.ensureDonorProfileExists().subscribe();
  }

  private tryStartTiming(): void {
    if (
      this.hasCompleted ||
      !this.questionnaireId ||
      !this.questionnaire?.template_id
    ) {
      return;
    }

    const sectionId = this.getCurrentSectionId();
    this.ensureTimingStarted("questionnaire", "questionnaire_started", null);
    this.ensureTimingStarted("section", "section_started", sectionId);
  }

  private ensureTimingStarted(
    scope: "questionnaire" | "section",
    eventType: string,
    sectionId: string | null
  ): void {
    const startedAtKey = this.getTimingStartedAtKey(scope, sectionId);
    const startedEventKey = this.getTimingStartedEventKey(scope, sectionId);
    let startedAt = localStorage.getItem(startedAtKey);

    if (!startedAt) {
      startedAt = new Date().toISOString();
      localStorage.setItem(startedAtKey, startedAt);
    }

    if (!localStorage.getItem(startedEventKey)) {
      this.recordTimingEvent(eventType, startedAt, sectionId);
      localStorage.setItem(startedEventKey, "true");
    }
  }

  private completeTiming(
    scope: "questionnaire" | "section",
    eventType: string,
    sectionId: string | null
  ): void {
    const startedAtKey = this.getTimingStartedAtKey(scope, sectionId);
    const startedEventKey = this.getTimingStartedEventKey(scope, sectionId);
    const startedAt = localStorage.getItem(startedAtKey);

    if (!startedAt) {
      return;
    }

    const endedAt = new Date().toISOString();
    this.recordTimingEvent(eventType, startedAt, sectionId, endedAt);
    localStorage.removeItem(startedAtKey);
    localStorage.removeItem(startedEventKey);
  }

  private recordTimingEvent(
    eventType: string,
    startedAt: string,
    sectionId: string | null,
    endedAt?: string
  ): void {
    if (!this.questionnaireId || !this.questionnaire?.template_id) {
      return;
    }

    const payload = {
      questionnaireId: this.questionnaireId,
      templateId: this.questionnaire.template_id,
      sectionId,
      subsectionId: null,
      eventType,
      startedAt,
      locale: this.localeService.currentLocale,
      ...(endedAt
        ? {
            endedAt,
            durationMs:
              new Date(endedAt).getTime() - new Date(startedAt).getTime(),
          }
        : {}),
    };

    this.questionnaireService
      .recordQuestionnaireTiming(payload)
      .pipe(
        catchError((error) => {
          console.error("Questionnaire timing failed:", error);
          return of(null);
        })
      )
      .subscribe();
  }

  private getTimingStartedAtKey(
    scope: "questionnaire" | "section",
    sectionId: string | null
  ): string {
    return [
      this.timingStoragePrefix,
      this.questionnaireId,
      this.questionnaire?.template_id,
      scope,
      sectionId || "all",
      "startedAt",
    ].join(":");
  }

  private getTimingStartedEventKey(
    scope: "questionnaire" | "section",
    sectionId: string | null
  ): string {
    return [
      this.timingStoragePrefix,
      this.questionnaireId,
      this.questionnaire?.template_id,
      scope,
      sectionId || "all",
      "startedEventSent",
    ].join(":");
  }

  private getCurrentSectionId(): string | null {
    return this.questionnaire?.sections?.[0]?.section_id || null;
  }

  convertNgbDateToString(date: any): string | null {
    if (!date) return null;
    const mm = date.month.toString().padStart(2, "0");
    const dd = date.day.toString().padStart(2, "0");
    const yyyy = date.year.toString();
    return `${mm}/${dd}/${yyyy}`;
  }

  startCountdown() {
    this.redirectTime = 3;
    const interval = setInterval(() => {
      this.redirectTime--;

      const timerElement = document.getElementById("redirect-timer");
      if (timerElement) {
        timerElement.textContent = this.redirectTime.toString();
      }

      if (this.redirectTime <= 0) {
        clearInterval(interval);

        if (this.isIneligible) {
          this.resService
            .setUserEligibility(
              this.questionnaireId!,
              false,
              this.ineligibilityMessage
            )
            .subscribe({
              next: (response) => {
                this.navigationService.setIneligibilityDetails(
                  {
                    message: this.ineligibilityMessage
                  }
                  
                );
                this.router
                  .navigate(["/questionnaire/postinitialineligible"])
                  .then(() => {
                    this.locationStrategy.replaceState(
                      {},
                      "",
                      "/questionnaire/postinitialineligible",
                      ""
                    );
                  });
                return;
              },
              error: (error) => {
                console.error(error);
                this.navigationService.setIneligibilityDetails({
                  message: this.ineligibilityMessage
                }
                  
                );
                this.router
                  .navigate(["/questionnaire/postinitialineligible"])
                  .then(() => {
                    this.locationStrategy.replaceState(
                      {},
                      "",
                      "/questionnaire/postinitialineligible",
                      ""
                    );
                  });
              },
            });
        } else {
          // TODO: Set user as eligible!!!!

          this.resService
            .setUserEligibility(
              this.questionnaireId!,
              true,
              this.ineligibilityMessage
            )
            .subscribe({
              next: (response) => {
                this.router
                  .navigate(["/questionnaire/postinitial"])
                  .then(() => {
                    this.locationStrategy.replaceState(
                      {},
                      "",
                      "/questionnaire/postinitial",
                      ""
                    );
                  });
              },
              error: (error) => {
                console.log(error);
                // TODO: Show error message ? 
              }
            });
        }
      }
    }, 1000);
  }

  getAnswerValue(
    question: Question,
    submittedAnswers: Record<string, any>
  ): string | string[] | null {
    let answer: string | string[] | null;

    if (question.question_type === "multi") {
      answer = submittedAnswers[question.question_id] || [];
    } else if (question.question_type === "date") {
      answer = this.convertNgbDateToString(
        submittedAnswers[question.question_id]
      );
    } else {
      answer = submittedAnswers[question.question_id];
    }
    if (this.isCurrencyQuestion(question) && typeof answer === "string") {
      answer = this.normalizeCurrencyAnswer(answer);
    }
    return this.trimAnswer(answer);
  }

  private isCurrencyQuestion(question: Question): boolean {
    const text = String(question.questiion_text || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

    return (
      /current.*income/.test(text) ||
      /household.*income/.test(text) ||
      /disposable.*income/.test(text) ||
      /ingreso.*actual/.test(text) ||
      /ingresos.*actuales/.test(text) ||
      /ingreso.*hogar/.test(text) ||
      /ingresos.*hogar/.test(text) ||
      /ingreso.*disponible/.test(text) ||
      /ingresos.*disponibles/.test(text)
    );
  }

  private normalizeCurrencyAnswer(answer: string): string {
    return answer.replace(/\D/g, "");
  }

  private trimAnswer(answer: string | string[] | null): string | string[] | null {
    if (Array.isArray(answer)) {
      return answer.map((value) =>
        typeof value === "string" ? value.trim() : value
      );
    }
    if (typeof answer === "string") {
      return answer.trim();
    }
    return answer;
  }

  extractProfileInfo(
    formData:
      | {
          sectionId: string;
          subsectionId: string;
          questionId: string;
          questionText: string;
          answer: string | string[] | null;
        }[]
      | undefined
  ): ProfileInfo {
    const donor: DonorProfile = {
      userId: this.authService.getUserId(),
      firstName: "",
      lastName: "",
      dateOfBirth: "",
      gender: "",
      ethnicity: "",
      relationshipToRecipient: "",
      bloodGroup: "",
      locale: this.localeService.currentLocale,
      preferredLocale: this.localeService.currentLocale,
    };

    const recipient: RecipientProfile = {
      donorUserId: this.authService.getUserId(),
      firstName: "",
      lastName: "",
      dateOfBirth: "",
      gender: "",
      ethnicity: "",
      causeOfEsrd: "",
      bloodGroup: "",
    };

    if (!formData) {
      return { data: { donor, recipient } };
    }

    for (const entry of formData) {
      const { questionText, answer } = entry;
      const lowerText = this.normalizeProfileQuestionText(questionText);
      const answerValue = this.answerToProfileValue(answer);
      const isRecipientQuestion =
        lowerText.includes("recipient") ||
        lowerText.includes("receptor") ||
        lowerText.includes("destinatario");

      // Grabbing Recipient Profile Info
      if (
        isRecipientQuestion &&
        (lowerText.includes("first name") || lowerText.includes("nombre"))
      ) {
        recipient.firstName = answerValue;
      } else if (
        isRecipientQuestion &&
        (lowerText.includes("last name") || lowerText.includes("apellido"))
      ) {
        recipient.lastName = answerValue;
      } else if(
        isRecipientQuestion &&
        (lowerText.includes("date of birth") ||
          lowerText.includes("fecha de nacimiento"))
      ){
        recipient.dateOfBirth = answer as string;
      }
      // Grabbing Donor Profile Info
      else
      {
        const mappedField = this.getDonorProfileFieldFromQuestionText(lowerText);

        if (mappedField) {
          if (mappedField === "dateOfBirth") {
            donor.dateOfBirth= answer as string
          } else {
            (donor as DonorProfile)[mappedField] = answerValue;
          }
        }
      }
    }

    return { data: { donor, recipient } };
  }

  private getDonorProfileFieldFromQuestionText(
    lowerText: string
  ): keyof DonorProfile | null {
    if (
      lowerText.includes("relationship") ||
      lowerText.includes("relacion") ||
      lowerText.includes("parentesco") ||
      lowerText.includes("vinculo")
    ) {
      return "relationshipToRecipient";
    }

    if (lowerText.includes("recipient") || lowerText.includes("receptor")) {
      return null;
    }

    if (lowerText.includes("first name") || lowerText === "nombre") {
      return "firstName";
    }

    if (lowerText.includes("last name") || lowerText.includes("apellido")) {
      return "lastName";
    }

    if (
      lowerText.includes("your date of birth") ||
      lowerText.includes("date of birth") ||
      lowerText.includes("fecha de nacimiento")
    ) {
      return "dateOfBirth";
    }

    if (
      (lowerText.includes("sex") && !lowerText.includes("sex change")) ||
      lowerText.includes("sexo")
    ) {
      return "gender";
    }

    if (lowerText.includes("ethnicity") || lowerText.includes("etnia")) {
      return "ethnicity";
    }

    if (
      lowerText.includes("blood type") ||
      lowerText.includes("tipo de sangre")
    ) {
      return "bloodGroup";
    }

    return null;
  }

  private normalizeProfileQuestionText(text: string): string {
    return String(text || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();
  }

  private answerToProfileValue(answer: string | string[] | null): string {
    return Array.isArray(answer) ? answer.join(", ") : answer || "";
  }

  isSubmitting = false;

  private checkIneligibility(formData: any[]): boolean {
    let weightInLbs: number | null = null;
    let heightFeet: number | null = null;
    let heightInches: number | null = null;
    let bmiThreshold: number | null = null;
    let bmiMessage = "";

    for (const entry of formData) {
      if (!entry.ineligibilityLogic) continue; // Skip if no ineligibility logic

      const { operator, value, conditionType, message } =
        entry.ineligibilityLogic;

      if (operator === "equals" && entry.answer === value) {
        this.ineligibilityMessage = message;

        return true;
      }

      if (operator === "less_than" && conditionType === "date") {
        const age = parseInt(calculateAge(entry.answer));

        if (age < value) {
          // `value` should be 18 for the age check
          this.ineligibilityMessage = message;
          return true;
        }
      }

      if (operator === "greater_than" && conditionType === "bmi") {
        // I have weight in lbs, need to find height.
        bmiThreshold = value;
        bmiMessage = message;
      }

      if (entry.questionText.toLowerCase() == "pounds") {
        weightInLbs = parseFloat(entry.answer);
      }

      if (entry.questionText.toLowerCase() == "feet") {
        heightFeet = parseFloat(entry.answer);
      }

      if (entry.questionText.toLowerCase() == "inches") {
        heightInches = parseFloat(entry.answer);
      }

      if (operator === "intersects" && Array.isArray(entry.answer)) {
        const selectedAnswers = new Set(entry.answer);
        const ineligibleAnswers = new Set(value);

        if (
          [...selectedAnswers].some((answer) => ineligibleAnswers.has(answer))
        ) {
          this.ineligibilityMessage = message;
          return true;
        }
      }
    }

    if (
      bmiThreshold &&
      weightInLbs !== null &&
      heightFeet !== null &&
      heightInches !== null
    ) {
      const heightInInches = heightFeet * 12 + heightInches;
      const bmi = (weightInLbs * 703) / (heightInInches * heightInInches);

      if (bmi > bmiThreshold) {
        this.ineligibilityMessage = bmiMessage;
        return true;
      }
    }

    return false; // User is eligible
  }

  onSubmit(submittedAnswers: Record<string, any>) {
    if (this.isSubmitting) {
      return; // Prevent multiple submissions
    }

    this.isSubmitting = true;

    if (this.questionnaireForm.valid) {
      this.isSavingResponses = true;

      const formData = this.questionnaire?.sections.flatMap((section) =>
        section.subsections.flatMap((subsection) =>
          subsection.questions.map((question) => ({
            sectionId: section.section_id,
            subsectionId: subsection.subsection_id,
            questionId: question.question_id,
            questionText: question.questiion_text,
            answer: this.getAnswerValue(
              question,
              submittedAnswers["submittedAnswers"]
            ),
            ineligibilityLogic: question.ineligibility_logic || null,
          }))
        )
      );

      if (formData && this.questionnaireId) {
        const profileInfo: ProfileInfo = this.extractProfileInfo(formData);

        this.resService
          .storeResponses(this.questionnaireId, formData) // Step 1: Store responses
          .pipe(
            switchMap((data) => {
              const sectionId = this.getCurrentSectionId();
              this.completeTiming("section", "section_completed", sectionId);
              this.completeTiming(
                "questionnaire",
                "questionnaire_completed",
                null
              );

              // Step 2: Update Donor and Recipient Profile Info
              this.isIneligible = this.checkIneligibility(formData);

              const updatedStatus = this.isIneligible
                ? "Under Review"
                : data.statusUpdated;

              const updateProfile$ =
                this.contentService.updateDonoranddRecipientProfileInfo(
                  profileInfo
                );
              const updateJourney$ =
                this.contentService.updateJourneySectionStatusForUser(
                  this.donorSectionId,
                  updatedStatus
                );

              const baseUrl = window.location.origin;
              const notificationRequest: NotificationRequest = {
                triggerEvent: "donor_submission",
                channel: "email",
                mailingListName: "coordinator-notifications",
                priority: "medium",
                data: {
                  donorName: `${profileInfo.data.donor.firstName} ${profileInfo.data.donor.lastName}`,
                  submissionDate: formatSubmissionDate(new Date()),
                  reviewLink: `${baseUrl}/hospital/donor/${profileInfo.data.donor.userId}/journey`,
                },
              };

              const sendNotification$ = this.notificationsService
                .sendNotification(notificationRequest)
                .pipe(
                  catchError((error) => {
                    console.error(
                      "Notification failed, but continuing:",
                      error
                    );
                    return of(null); // Ensures flow continues even if notification fails
                  })
                );

              return forkJoin([
                updateProfile$,
                updateJourney$,
                sendNotification$,
              ]);
            }),
            catchError((error) => {
              console.error("Error during processing:", error);
              this.isSavingResponses = false;
              return []; // Return an empty observable to gracefully handle the error
            })
          )
          .subscribe({
            next: () => {
              // call api to check regret conditions
              this.isSavingResponses = false;
              this.hasCompleted = true;
              this.startCountdown();
            },
            error: (error) => {
              console.error("Error in the final step:", error);
            },
          });
      } else {
        console.error("Form is invalid");
      }
    }
  }
}
